import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList } from 'recharts';
import {
  Package,
  AlertCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  FileBarChart,
  FileText,
  List,
  Calendar
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from 'react-router-dom';

interface DashboardStats {
  totalProducts: number;
  lowStockItems: number;
  itemsReceived: number;
  itemsIssued: number;
}

interface OrderStats {
  secretariat: string;
  count: number;
}

interface TopProductStats {
  product_name: string;
  total_quantity: number;
  approved_quantity: number;
  pending_quantity: number;
  rejected_quantity: number;
}

interface InventoryItem {
  id: string;
  quantity: number;
  initial_quantity: number;
  product: {
    id: string;
    name: string;
    min_quantity: number;
  };
}

interface Order {
  id: string;
  created_at: string;
  quantity: number;
  status: string;
  product: {
    id: string;
    name: string;
  };
}

interface RecentOrder {
  id: string;
  order_number: string;
  responsible_name: string;
  construction_name: string;
  created_at: string;
  status: string;
  department: {
    name: string;
  };
}

const COLORS = [
  '#10B981', // verde
  '#F59E0B', // amarelo
  '#EF4444', // vermelho
  '#6366F1', // indigo
  '#EC4899', // rosa
  '#8B5CF6', // roxo
  '#14B8A6', // teal
  '#F97316', // laranja
  '#06B6D4', // cyan
  '#84CC16'  // lime
];

const SECRETARIAT_ACRONYMS: { [key: string]: string } = {
  'Secretaria Municipal de Educação': 'SME',
  'Secretaria Municipal de Saúde': 'SMS',
  'Secretaria Municipal de Administração': 'SEMAD',
  'Secretaria Municipal de Obras': 'SMO',
  'Secretaria Municipal de Assistência Social': 'SEMAS',
  'Secretaria Municipal de Cultura': 'SMC',
  'Secretaria Municipal de Esporte e Lazer': 'SMEL',
  'Secretaria Municipal de Meio Ambiente': 'SEMMA',
  'Secretaria Municipal de Agricultura': 'SMA',
  'Secretaria Municipal de Planejamento': 'SEMPLAN',
  'Secretaria Municipal de Finanças': 'SMF',
  'Secretaria Municipal de Transportes': 'SMT',
  'Não especificada': 'N/E'
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatSelectedMonth = (month: string) => {
  const [year, monthNum] = month.split('-');
  const date = new Date(parseInt(year), parseInt(monthNum) - 1);
  return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
};

const getMonthOptions = () => {
  const options = [];
  const currentDate = new Date();
  
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  
  return options;
};

const Dashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  
  // Estados para dados
  const [stats, setStats] = useState<DashboardStats>({
    totalProducts: 0,
    lowStockItems: 0,
    itemsReceived: 0,
    itemsIssued: 0,
  });
  
  const [topProducts, setTopProducts] = useState<TopProductStats[]>([]);
  const [recentProducts, setRecentProducts] = useState<any[]>([]);
  const [recentPendingOrders, setRecentPendingOrders] = useState<any[]>([]);
  const [recentApprovedOrders, setRecentApprovedOrders] = useState<any[]>([]);
  const [recentMovements, setRecentMovements] = useState<any[]>([]);
  const [orderStats, setOrderStats] = useState<OrderStats[]>([]);
  
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingOrdersPage, setPendingOrdersPage] = useState(1);
  const [approvedOrdersPage, setApprovedOrdersPage] = useState(1);
  const [productsPage, setProductsPage] = useState(1);
  const [totalMovements, setTotalMovements] = useState(0);
  const [totalPendingOrders, setTotalPendingOrders] = useState(0);
  const [totalApprovedOrders, setTotalApprovedOrders] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  
  const itemsPerPage = 5;

  useEffect(() => {
    fetchDashboardData();
    fetchRecentApprovedOrders();
    fetchRecentPendingOrders();
    fetchRecentProducts();
    fetchRecentOrders();
  }, [selectedMonth]);

  useEffect(() => {
    fetchRecentMovements();
  }, [currentPage, selectedMonth]);

  const fetchDashboardData = async () => {
    try {
      // Pegar o primeiro e último dia do mês selecionado
      const [year, month] = selectedMonth.split('-');
      const firstDay = new Date(parseInt(year), parseInt(month) - 1, 1);
      const lastDay = new Date(parseInt(year), parseInt(month), 0);
      
      const startDate = firstDay.toISOString();
      const endDate = lastDay.toISOString();

      // Buscar total de produtos únicos no inventário
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('id, name, min_quantity')
        .order('created_at', { ascending: false });

      if (productsError) throw productsError;

      // Buscar inventário para cada produto
      if (productsData && productsData.length > 0) {
        const { data: inventoryData } = await supabase
          .from('inventory')
          .select('product_id, quantity')
          .in('product_id', productsData.map(p => p.id));

        // Criar mapa de inventário
        const inventoryMap = inventoryData?.reduce((acc, inv) => {
          acc[inv.product_id] = inv;
          return acc;
        }, {});

        // Combinar dados
        const enrichedProducts = productsData.map(product => ({
          ...product,
          inventory: inventoryMap[product.id] ? [inventoryMap[product.id]] : []
        }));

        // Calcular total de produtos (apenas produtos com inventário) e produtos com estoque baixo
        const productsWithInventory = enrichedProducts.filter(product => product.inventory && product.inventory.length > 0);
        const totalProducts = productsWithInventory.length;
        const lowStockItems = productsWithInventory.filter(
          (product) => 
            product.inventory?.[0]?.quantity <= product.min_quantity
        ).length;

        // Buscar transações do mês
        const { data: transactions, error: transactionsError } = await supabase
          .from('transactions')
          .select('id, type, quantity, product_id')
          .gte('created_at', startDate)
          .lte('created_at', endDate);

        if (transactionsError) throw transactionsError;

        // Buscar produtos relacionados às transações
        const transactionProductIds = [...new Set(transactions?.map(t => t.product_id) || [])];
        const { data: transactionProducts } = await supabase
          .from('products')
          .select('id, name')
          .in('id', transactionProductIds);

        // Criar mapa de produtos
        const productsMap = transactionProducts?.reduce((acc, prod) => {
          acc[prod.id] = prod;
          return acc;
        }, {});

        // Calcular totais de entrada e saída
        const itemsReceived = transactions
          ?.filter((t) => t.type === 'entrada')
          .reduce((sum, t) => sum + t.quantity, 0) || 0;

        const itemsIssued = transactions
          ?.filter((t) => t.type === 'saida')
          .reduce((sum, t) => sum + t.quantity, 0) || 0;

        // Processar dados dos produtos mais solicitados
        const productStats: { [key: string]: TopProductStats } = {};

        transactions?.forEach((transaction) => {
          const product = productsMap[transaction.product_id];
          if (!product) return;

          const productName = product.name;
          if (!productStats[productName]) {
            productStats[productName] = {
              product_name: productName,
              total_quantity: 0,
              approved_quantity: 0,
              pending_quantity: 0,
              rejected_quantity: 0
            };
          }

          productStats[productName].total_quantity += transaction.quantity;
          
          if (transaction.type === 'entrada') {
            productStats[productName].approved_quantity += transaction.quantity;
          } else if (transaction.type === 'saida') {
            productStats[productName].rejected_quantity += transaction.quantity;
          }
        });

        const topProductsList = Object.values(productStats)
          .sort((a, b) => b.total_quantity - a.total_quantity)
          .slice(0, 5);

        setStats({
          totalProducts,
          lowStockItems,
          itemsReceived,
          itemsIssued
        });

        setTopProducts(topProductsList);
      }

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados do dashboard",
        variant: "destructive",
      });
    }
  };

  const fetchRecentMovements = async () => {
    try {
      const [year, month] = selectedMonth.split('-').map(Number);
      const startDate = new Date(Date.UTC(year, month - 1, 1));
      const endDate = new Date(Date.UTC(year, month, 0));

      const { data, count } = await supabase
        .from('transactions')
        .select('*', { count: 'exact' })
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString())
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

      if (data) {
        setRecentMovements(data);
        setTotalMovements(count || 0);
      }
    } catch (error) {
      console.error('Erro ao carregar movimentações recentes:', error);
    }
  };

  const fetchRecentApprovedOrders = async () => {
    try {
      const [year, month] = selectedMonth.split('-').map(Number);
      const startDate = new Date(Date.UTC(year, month - 1, 1));
      const endDate = new Date(Date.UTC(year, month, 0));

      const { data: orders } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          responsible_name,
          construction_name,
          created_at,
          status,
          departments (
            name
          )
        `)
        .eq('status', 'approved')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(5);

      if (orders) {
        setRecentApprovedOrders(orders);
      }
    } catch (error) {
      console.error('Erro ao carregar pedidos aprovados:', error);
    }
  };

  const fetchRecentPendingOrders = async () => {
    try {
      const { data: orders } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          responsible_name,
          construction_name,
          created_at,
          status,
          departments (
            name
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(5);

      if (orders) {
        setRecentPendingOrders(orders);
      }
    } catch (error) {
      console.error('Erro ao carregar pedidos pendentes:', error);
    }
  };

  const fetchRecentProducts = async () => {
    try {
      // Pegar o primeiro e último dia do mês selecionado
      const [year, month] = selectedMonth.split('-');
      const firstDay = new Date(parseInt(year), parseInt(month) - 1, 1);
      const lastDay = new Date(parseInt(year), parseInt(month), 0);
      
      const startDate = firstDay.toISOString();
      const endDate = lastDay.toISOString();

      // Buscar produtos adicionados no mês selecionado
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('id, name, description, created_at, min_quantity')
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('created_at', { ascending: false });

      if (productsError) throw productsError;

      if (products && products.length > 0) {
        // Buscar inventário para cada produto
        const { data: inventoryData } = await supabase
          .from('inventory')
          .select('product_id, quantity')
          .in('product_id', products.map(p => p.id));

        // Criar mapa de inventário
        const inventoryMap = inventoryData?.reduce((acc, inv) => {
          acc[inv.product_id] = inv;
          return acc;
        }, {});

        // Combinar dados
        const enrichedProducts = products.map(product => ({
          ...product,
          inventory: inventoryMap[product.id] ? [inventoryMap[product.id]] : []
        }));

        // Aplicar paginação
        const startIndex = (productsPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedProducts = enrichedProducts.slice(startIndex, endIndex);

        setRecentProducts(paginatedProducts);
        setTotalProducts(enrichedProducts.length);
      } else {
        setRecentProducts([]);
        setTotalProducts(0);
      }

    } catch (error) {
      console.error('Erro ao carregar produtos recentes:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os produtos recentes",
        variant: "destructive",
      });
    }
  };

  const fetchRecentOrders = async () => {
    try {
      // Verificar se o usuário está autenticado
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('Erro de sessão:', sessionError || 'Sessão não encontrada');
        toast({
          title: "Sessão expirada",
          description: "Por favor, faça login novamente",
          variant: "destructive",
        });
        navigate('/admin-login');
        return;
      }

      // Verificar o usuário atual
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.error('Erro de autenticação:', authError || 'Usuário não encontrado');
        toast({
          title: "Erro de autenticação",
          description: "Por favor, faça login novamente",
          variant: "destructive",
        });
        navigate('/admin-login');
        return;
      }

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          *,
          department:departments (
            name
          ),
          order_items (
            id,
            quantity,
            product:products (
              id,
              name
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (ordersError) {
        console.error('Erro ao buscar pedidos:', ordersError);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os pedidos recentes",
          variant: "destructive",
        });
        return;
      }

      if (ordersData) {
        // Enriquecer os dados dos pedidos
        const enrichedOrders = ordersData.map(order => ({
          ...order,
          department: order.department || { name: 'Não especificado' }
        }));

        // Separar pedidos por status
        const allPending = enrichedOrders.filter(order => order.status === 'pending') || [];
        const allApproved = enrichedOrders.filter(order => order.status === 'approved') || [];

        // Aplicar paginação
        const pendingStartIndex = (pendingOrdersPage - 1) * itemsPerPage;
        const pendingEndIndex = pendingStartIndex + itemsPerPage;
        const paginatedPending = allPending.slice(pendingStartIndex, pendingEndIndex);

        const approvedStartIndex = (approvedOrdersPage - 1) * itemsPerPage;
        const approvedEndIndex = approvedStartIndex + itemsPerPage;
        const paginatedApproved = allApproved.slice(approvedStartIndex, approvedEndIndex);

        // Calcular estatísticas por secretaria
        const secretariatStats = enrichedOrders.reduce((acc: { [key: string]: number }, order) => {
          const secretariat = SECRETARIAT_ACRONYMS[order.department?.name] || 'Não especificada';
          acc[secretariat] = (acc[secretariat] || 0) + 1;
          return acc;
        }, {});

        const formattedStats = Object.entries(secretariatStats).map(([name, count]) => ({
          secretariat: name,
          count
        }));

        // Atualizar estados
        setRecentPendingOrders(paginatedPending);
        setRecentApprovedOrders(paginatedApproved);
        setTotalPendingOrders(allPending.length);
        setTotalApprovedOrders(allApproved.length);
        setOrderStats(formattedStats);
      } else {
        setRecentPendingOrders([]);
        setRecentApprovedOrders([]);
        setTotalPendingOrders(0);
        setTotalApprovedOrders(0);
        setOrderStats([]);
      }
    } catch (error) {
      console.error('Erro ao carregar pedidos recentes:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os pedidos recentes",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchRecentProducts();
  }, [productsPage]);

  useEffect(() => {
    fetchRecentOrders();
  }, [pendingOrdersPage, approvedOrdersPage]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center flex-col sm:flex-row gap-4">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <Select
          value={selectedMonth}
          onValueChange={setSelectedMonth}
        >
          <SelectTrigger className="w-[200px]">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Selecione o mês" />
          </SelectTrigger>
          <SelectContent>
            {getMonthOptions().map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Card - Total de Produtos */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm font-medium text-blue-700">
                Total de Produtos
              </CardTitle>
              <p className="text-xs text-blue-600 mt-1">
                Produtos cadastrados
              </p>
            </div>
            <Package className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{stats.totalProducts}</div>
          </CardContent>
        </Card>

        {/* Card - Produtos com Estoque Baixo */}
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm font-medium text-amber-700">
                Estoque Baixo
              </CardTitle>
              <p className="text-xs text-amber-600 mt-1">
                Produtos precisando de reposição
              </p>
            </div>
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-700">{stats.lowStockItems}</div>
          </CardContent>
        </Card>

        {/* Card - Itens Recebidos */}
        <Card className="bg-green-50 border-green-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm font-medium text-green-700">
                Itens Recebidos
              </CardTitle>
              <p className="text-xs text-green-600 mt-1">
                Em {formatSelectedMonth(selectedMonth)}
              </p>
            </div>
            <ArrowDownCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{stats.itemsReceived}</div>
          </CardContent>
        </Card>

        {/* Card - Itens Retirados */}
        <Card className="bg-red-50 border-red-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm font-medium text-red-700">
                Itens Retirados
              </CardTitle>
              <p className="text-xs text-red-600 mt-1">
                Em {formatSelectedMonth(selectedMonth)}
              </p>
            </div>
            <ArrowUpCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{stats.itemsIssued}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna da Esquerda */}
        <div className="lg:col-span-2">
          {/* Card - Produtos Mais Solicitados */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg sm:text-xl">Produtos Mais Solicitados</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Top 5 produtos mais solicitados em {formatSelectedMonth(selectedMonth)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topProducts} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="product_name" 
                      width={150}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value} unidades`, 'Quantidade Total']}
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Bar 
                      dataKey="total_quantity" 
                      name="Quantidade Total" 
                      fill={COLORS[0]}
                      radius={[4, 4, 4, 4]}
                    >
                      {topProducts.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                      <LabelList
                        dataKey="total_quantity"
                        position="right"
                        formatter={(value) => `${value} unid.`}
                        style={{ fill: '#6b7280', fontSize: '12px' }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {topProducts.length === 0 && (
                <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                  <Package className="h-12 w-12 mb-2 opacity-50" />
                  <p>Nenhum produto solicitado no período</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Coluna da Direita */}
        <div className="space-y-6">
          {/* Card - Pedidos Pendentes */}
          <Card className="col-span-4">
            <CardHeader>
              <CardTitle>Pedidos Pendentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {recentPendingOrders.map((order) => (
                  <div key={order.id} className="flex flex-col space-y-4">
                    <div className="flex items-center">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback>
                          {order.order_number?.toString().substring(0, 2) || 'OR'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="ml-4 space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {order.responsible_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {order.construction_name}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <span>{order.department?.name || 'N/A'}</span>
                          <span>•</span>
                          <span>{new Date(order.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                      <div className="ml-auto">
                        <Badge variant="secondary">Pendente</Badge>
                      </div>
                    </div>
                    {order.items && order.items.length > 0 && (
                      <div className="ml-13 pl-4 border-l-2 border-gray-200">
                        <p className="text-sm font-medium mb-2">Itens do Pedido:</p>
                        <div className="space-y-2">
                          {order.items.map((item, index) => (
                            <div key={index} className="text-sm">
                              <span className="font-medium">{item.product.name}</span>
                              <span className="text-muted-foreground"> - {item.quantity} {item.product.unit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {recentPendingOrders.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                    <FileText className="h-8 w-8 mb-2 opacity-50" />
                    <p>Nenhum pedido pendente</p>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-center">
              <div className="flex items-center justify-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPendingOrdersPage((old) => Math.max(old - 1, 1));
                    fetchRecentOrders();
                  }}
                  disabled={pendingOrdersPage === 1}
                >
                  Anterior
                </Button>
                <div className="text-sm text-muted-foreground">
                  Página {pendingOrdersPage} de{" "}
                  {Math.ceil(totalPendingOrders / itemsPerPage)}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPendingOrdersPage((old) => old + 1);
                    fetchRecentOrders();
                  }}
                  disabled={
                    pendingOrdersPage === Math.ceil(totalPendingOrders / itemsPerPage)
                  }
                >
                  Próxima
                </Button>
              </div>
            </CardFooter>
          </Card>

          {/* Card - Pedidos Aprovados */}
          <Card className="col-span-4">
            <CardHeader>
              <CardTitle>Pedidos Aprovados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {recentApprovedOrders.map((order) => (
                  <div key={order.id} className="flex flex-col space-y-4">
                    <div className="flex items-center">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback>
                          {order.order_number?.toString().substring(0, 2) || 'OR'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="ml-4 space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {order.responsible_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {order.construction_name}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <span>{order.department?.name || 'N/A'}</span>
                          <span>•</span>
                          <span>{new Date(order.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                      <div className="ml-auto">
                        <Badge variant="secondary">Aprovado</Badge>
                      </div>
                    </div>
                    {order.items && order.items.length > 0 && (
                      <div className="ml-13 pl-4 border-l-2 border-gray-200">
                        <p className="text-sm font-medium mb-2">Itens do Pedido:</p>
                        <div className="space-y-2">
                          {order.items.map((item, index) => (
                            <div key={index} className="text-sm">
                              <span className="font-medium">{item.product.name}</span>
                              <span className="text-muted-foreground"> - {item.quantity} {item.product.unit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {recentApprovedOrders.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                    <FileText className="h-8 w-8 mb-2 opacity-50" />
                    <p>Nenhum pedido aprovado</p>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-center">
              <div className="flex items-center justify-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setApprovedOrdersPage((old) => Math.max(old - 1, 1));
                    fetchRecentOrders();
                  }}
                  disabled={approvedOrdersPage === 1}
                >
                  Anterior
                </Button>
                <div className="text-sm text-muted-foreground">
                  Página {approvedOrdersPage} de{" "}
                  {Math.ceil(totalApprovedOrders / itemsPerPage)}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setApprovedOrdersPage((old) => old + 1);
                    fetchRecentOrders();
                  }}
                  disabled={
                    approvedOrdersPage === Math.ceil(totalApprovedOrders / itemsPerPage)
                  }
                >
                  Próxima
                </Button>
              </div>
            </CardFooter>
          </Card>

          {/* Card - Produtos Recentes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base sm:text-lg">Produtos Recentes</CardTitle>
              <Package className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentProducts.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm sm:text-base">
                          {product.name}
                        </p>
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                          {product.inventory?.[0]?.quantity || 0} unid.
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <span>{product.category_id}</span>
                        <span>•</span>
                        <span>{new Date(product.created_at).toLocaleDateString('pt-BR')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {recentProducts.length === 0 && (
                <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                  <Package className="h-8 w-8 mb-2 opacity-50" />
                  <p>Nenhum produto cadastrado recentemente</p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-center">
              <div className="flex items-center justify-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setProductsPage((old) => Math.max(old - 1, 1));
                    fetchRecentProducts();
                  }}
                  disabled={productsPage === 1}
                >
                  Anterior
                </Button>
                <div className="text-sm text-muted-foreground">
                  Página {productsPage} de{" "}
                  {Math.ceil(totalProducts / itemsPerPage)}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setProductsPage((old) => old + 1);
                    fetchRecentProducts();
                  }}
                  disabled={
                    productsPage === Math.ceil(totalProducts / itemsPerPage)
                  }
                >
                  Próxima
                </Button>
              </div>
            </CardFooter>
          </Card>

          {/* Card - Movimentações Recentes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base sm:text-lg">Movimentações Recentes</CardTitle>
              <List className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentMovements.map((movement) => (
                  <div
                    key={movement.id}
                    className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0"
                  >
                    <div>
                      <p className="font-medium text-sm sm:text-base">{movement.product_name}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">{new Date(movement.created_at).toLocaleDateString('pt-BR')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          movement.type === "entrada"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {movement.type === "entrada" ? 'Entrada' : 'Saída'}
                      </span>
                      <span className="font-medium text-sm sm:text-base">{movement.quantity}</span>
                    </div>
                  </div>
                ))}

                {recentMovements.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                    <List className="h-8 w-8 mb-2 opacity-50" />
                    <p>Nenhuma movimentação no período</p>
                  </div>
                )}
              </div>

              {/* Controles de Paginação */}
              <div className="flex items-center justify-between space-x-2 pt-4">
                <div className="text-sm text-gray-500">
                  Mostrando {recentMovements.length} de {totalMovements} movimentações
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage * itemsPerPage >= totalMovements}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;